import React from 'react';
import { styled } from '@mui/material';
import {
  WBFlex,
  WBSvgIcon,
  WBTypography,
  WBButton,
  WBIcon,
  WBBox,
  WBBadge,
} from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import {
  FILE_TYPES,
  PDF_FILE_EXTENSIONS,
  S3Upload,
} from '@admiin-com/ds-common';
import { WBS3MediaDragDrop as S3MediaDragDrop } from '@admiin-com/ds-amplify-web';
import UploadIcon from '../../../../assets/icons/upload.svg';
import { useTheme } from '@mui/material';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags/useFeatureFlags';
import { ForwardBillsContainer } from '../ForwardBills';
import {
  DocumentAnalysis,
  DocumentAnalysisStatus,
} from '@admiin-com/ds-graphql';
import { Bill } from '../PayBillCreateForm';
import { getVendorName } from '../../../helpers/tasks';
import { XeroButton } from './XeroButton';
import { useTaskCreationContext } from '../../../features/providers/TaskCreationProvider/useTaskCreationContext';
import { useDocumentAnalysisByEntityStatus } from '../../../features/hooks/useForwardedBills/useForwardedBills';
import { useEntityId } from '../../../features/hooks/useEntityContext/useEntityContext';

interface TaskUploadProps {
  bills: Bill[];
  setBills: (bills: Bill[]) => void;
  onChange: (images: S3Upload[]) => void;
  setLoading?: (loading: boolean) => void;
  onRemove?: (document: S3Upload) => void;
  label?: string;
  onUploadStarted?: () => void;
  onAdd?: (documentAnalysis: DocumentAnalysis[]) => void;
  includeNameInKey?: boolean;
  loading?: boolean;
}

export const TaskUpload = React.forwardRef<any, TaskUploadProps>(
  (
    {
      label,
      onChange,
      setLoading,
      onUploadStarted,
      onAdd,
      bills,
      onRemove,
      includeNameInKey,
      loading,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { featureFlags } = useFeatureFlags();
    const { taskType } = useTaskCreationContext();
    const entityId = useEntityId();

    // Fetch forwarded bills count for EMAIL source
    const { documentAnalysis: emailForwardedBills } =
      useDocumentAnalysisByEntityStatus({
        entityId,
        status: DocumentAnalysisStatus.SCANNED,
        source: 'EMAIL',
        skip: !entityId,
      });

    const forwardedBillsCount = emailForwardedBills?.length || 0;

    const extensions = Object.entries(FILE_TYPES)
      .filter(([key, value]) => PDF_FILE_EXTENSIONS.includes(value))
      .map(([key, value]) => key)
      .join(', ');
    const theme = useTheme();
    const [forwardBillsOpen, setForwardBillsOpen] = React.useState(false);
    const [forwardBillSource, setForwardBillSource] = React.useState<
      'EMAIL' | 'XERO'
    >('EMAIL');

    React.useEffect(() => {
      if (taskType === 'forwardBills') {
        setForwardBillsOpen(true);
        setForwardBillSource('EMAIL');
      }
    }, [taskType]);

    const UploadMessage = (
      <>
        <WBFlex gap={1}>
          <WBSvgIcon fontSize="small" color={theme.palette.primary.main}>
            <UploadIcon />
          </WBSvgIcon>
          <WBTypography
            textAlign={'center'}
            fontWeight={'bold'}
            variant="body2"
          >
            {t('uploadBill', { ns: 'taskbox' })}
          </WBTypography>
        </WBFlex>
        <WBTypography
          color={'text.secondary'}
          textAlign={'center'}
          variant="body2"
          mt={1}
        >
          {`${t('supportedFiles', { ns: 'taskbox' })} ${extensions}`}
        </WBTypography>
        <WBTypography
          color={'text.secondary'}
          textAlign={'center'}
          variant="body2"
        >
          {`${t('maxSize', { ns: 'taskbox', size: '10MB' })}`}
        </WBTypography>
        <DeviderContainer>
          <Devider flex={1} />
          <WBTypography textAlign={'center'} variant="body2">
            {t('or', { ns: 'common' })}
          </WBTypography>
          <Devider flex={1} />
        </DeviderContainer>
        <FormButtonContainer>
          {featureFlags?.xero && (
            <XeroButton
              onSucces={() => {
                setForwardBillsOpen(true);
                setForwardBillSource('XERO');
              }}
            />
          )}
          <WBBadge
            badgeContent={forwardedBillsCount}
            color="error"
            invisible={forwardedBillsCount === 0}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'error.main',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                minWidth: '20px',
                height: '20px',
              },
            }}
          >
            <FormButton
              startIcon={
                <WBIcon name="Mail" size="small" library="ioniconOutline" />
              }
              onClick={(e: any) => {
                e.stopPropagation();
                setForwardBillsOpen(true);
                setForwardBillSource('EMAIL');
              }}
              type="button"
            >
              <WBFlex alignItems="center" gap={1}>
                {t('forwardedEmails', { ns: 'authentication' })}
              </WBFlex>
            </FormButton>
          </WBBadge>
        </FormButtonContainer>
      </>
    );

    return (
      <WBFlex flexDirection={'column'} gap={2} ref={ref}>
        <S3MediaDragDrop
          height={'300px'}
          uploadBtnText=""
          maxFiles={10}
          includeNameInKey={includeNameInKey}
          maxSizeMB={10}
          onRemove={(file: S3Upload) => {
            onRemove?.(file);
          }}
          files={bills
            .filter((document) => document !== null)
            .map((bill: Bill) => ({
              key: bill.document?.key ?? '',
              identityId: bill.document?.identityId ?? '',
              level: 'private',
              type: bill.document?.type ?? 'PDF',
              name:
                bill.documentName ?? getVendorName(bill.documentAnalysis) ?? '',
              size: bill.documentSize ?? bill.documentAnalysis?.fromEmail ?? '',
              source:
                bill.documentAnalysis?.source === 'EMAIL' ? 'EMAIL' : 'UPLOAD',
            }))}
          validFileTypes={PDF_FILE_EXTENSIONS}
          inputAccept={PDF_FILE_EXTENSIONS.join(', ')}
          level="private"
          onUploaded={(images: S3Upload[]) => {
            onChange(images);
            if (setLoading) setLoading(false);
          }}
          onDropped={(files: FileList) => {
            if (setLoading) setLoading(true);
            if (onUploadStarted) onUploadStarted();
          }}
          uploadMessage={UploadMessage}
          uploadBtnTextProps={{
            variant: 'body2',
            fontWeight: 'bold',
            color: 'text.primary',
          }}
          removeConfirmationTitle={t('removeConfirmationTitle', {
            ns: 'taskbox',
          })}
          removeConfirmationDescription={t('removeConfirmationDescription', {
            ns: 'taskbox',
          })}
          loading={loading}
        />
        <ForwardBillsContainer
          source={forwardBillSource}
          open={forwardBillsOpen}
          onClose={() => setForwardBillsOpen(false)}
          onAdd={(documentAnalysis: DocumentAnalysis[]) => {
            setForwardBillsOpen(false);
            onAdd && onAdd(documentAnalysis);
          }}
        />
      </WBFlex>
    );
  }
);

const Devider = styled(WBBox)(({ theme }) => ({
  backgroundColor: theme.palette.text.primary,
  height: '1px',
  width: theme.spacing(25),
  margin: theme.spacing(4, 0),
  [theme.breakpoints.down('sm')]: {
    backgroundColor: theme.palette.text.primary,
    width: theme.spacing(10),
    height: '1px',
  },
}));

export const FormButton = styled(WBButton)(({ theme }) => ({
  display: 'border-box',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderColor: theme.palette.secondary.main,
  borderWidth: '1px',
  borderStyle: 'solid',
  '&:hover': {
    borderWidth: '1px',
  },
  [theme.breakpoints.down('sm')]: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    outerWidth: '100%',
  },
}));

const FormButtonContainer = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(4),
  backgroundColor: 'none',
  [theme.breakpoints.down('sm')]: {
    backgroundColor: 'none',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
}));

const DeviderContainer = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(4),
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing(4),
    width: '100%',
  },
}));
